<template>
  <v-form ref="mapForm" class="map-form" lazy-validation>
    <div class="d-flex flex-grow-1">
      <div class="form-wrapper">
        <div class="status-bar" v-if="isReviewing || isWaitingForDeletion">
          <status-box> </status-box>
        </div>
        <label class="map-label"
          >Click on the map to draw area corners; click the first corner or
          double-click to finish. Drag corners (or edge midpoints) to adjust
          borders. <br />Optional: Right-click to add entry, left-click on the
          same spot to remove it</label
        >
        <div class="form-label">Street address</div>
        <vue-google-autocomplete
          v-if="google"
          v-model="formData.street_address"
          v-on:placechanged="onPlaceChanged"
          id="map"
          ref="autocomplete"
          class="custom-suggestion"
          :class="{ 'has-error': hasError && !formData.street_address }"
          :disabled="disabled"
        >
        </vue-google-autocomplete>
        <v-text-field
          v-model="formData.street_address"
          ref="hiddenForm"
          class="hidden-form"
        />

        <div class="form-label">City</div>
        <v-autocomplete
          solo
          flat
          dense
          background-color="#F6F6F6"
          v-model="formData.city_id"
          :items="cityOptions"
          item-value="id"
          item-text="name"
          :filter="filterCity"
          :rules="[rules.required]"
          class="pkmn-input error-border city-select"
          placeholder="Search for a city"
          no-data-text="No matching city"
          :hint="cityHint"
          persistent-hint
          :disabled="disabled"
        />

        <div class="toggle-section">
          <label class="toggle-label">Show polygon in the ParkMan app map</label>
          <toggle-button v-model="formData.show_polygon" color="#37BD01" :sync="true"></toggle-button>
        </div>
      </div>
      <div
        class="map-container flex-grow-1"
        style="height: calc(100vh - 135px)"
      >
        <zones-map
          ref="zonesMap"
          :zoom="zoom"
          :zones="zones"
          :map-created="onMapCreated"
          map-type-id="roadmap"
          :options="mapOptions"
          :map-right-clicked="onMapRightClicked"
          :polygon-path-changed="onPolygonPathChanged"
          :zones-loaded="onZonesLoaded"
          :newCenter="center"
          :disabled="disabled"
          :centrePoint.sync="formData.centre_point" 
        >
          <gmap-marker
            v-for="m in filteredEntryPoints"
            :key="m.entry_point"
            :position="m.position"
            :icon="m.icon"
            :draggable="!disabled"
            @dragend="onDragendEntryPoint($event, m)"
            @click="onClickEntryPoint(m)"
          >
          </gmap-marker>
        </zones-map>
      </div>
    </div>
  </v-form>
</template>

<script>
import ZonesMap from '@/components/Common/ZonesMap.vue'
import { MAP_SETTINGS } from '@/helpers/map'
import math from '@/components/Map/calculations'
import helpers from '@/helpers'
import VueGoogleAutocomplete from 'vue-google-autocomplete'
import StatusBox from '@/components/Common/StatusBox.vue'
import { gmapApi } from 'vue2-google-maps'
import rules from '@/helpers/validation'

// Timezone each supported country's cities are expected to carry. The cities
// endpoint has no country field, so this is what ties a city to the merchant.
const COUNTRY_TIMEZONES = {
  FI: 'Europe/Helsinki',
  SE: 'Europe/Stockholm',
  DK: 'Europe/Copenhagen',
  IT: 'Europe/Rome',
  LT: 'Europe/Vilnius',
  EE: 'Europe/Tallinn',
  NO: 'Europe/Oslo',
  IS: 'Atlantic/Reykjavik'
  // US is left out on purpose: it spans several timezones
}

export default {
  components: {
    ZonesMap,
    VueGoogleAutocomplete,
    StatusBox
  },
  props: {
    zonesLoaded: {
      type: Function,
      default: () => {}
    },
    zones: null,
    zone: null,
    zoneId: {
      type: Number,
      default: -1
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hasError: false,
      formData: {
        street_address: null,
        postal_code: null,
        state: null,
        entry_points: [],
        polygon: null,
        centre_point: null,
        city_id: null,
        country_id: null,
        show_polygon: null
      },
      rules,
      formDataCopy: '',
      center: { lat: 60.165824, lng: 24.930189 },
      zoom: 2,
      marker: null,
      address: null,
      mapOptions: {
        styles: MAP_SETTINGS.styles,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        tilt: 0,
        fullscreenControlOptions: { position: 9 } // google.maps.ControlPosition.RIGHT_BOTTOM is just 9
      },
      mapClickListener: null,
      mapDblClickListener: null,
      mapMouseMoveListener: null,
      committedLine: null,
      rubberLine: null,
      drawingVertices: [],
      vertexMarkers: [],
      enableDrawingPolygon: false,
      selectedPolygon: null,
      entryPoints: [],
      polygonPath: '',
      polygon: null,
      geocoder: null,
      status_name: ''
    }
  },
  methods: {
    rulesValidation() {
      this.$nextTick(() => {
        this.hasError =
          this.$refs.hiddenForm.$el.classList.contains('error--text')
      })

      return this.$refs.mapForm.validate()
    },
    getZonePaths(zone) {
      const path = zone.polygon.split(',').map((coord) => {
        const [lat, lng] = coord.split(' ')
        return { lat: parseFloat(lat), lng: parseFloat(lng) }
      })
      return path
    },
    async onZonesLoaded(polygons) {
      for (let i = 0; i < polygons.length; i++) {
        this.enableDrawingPolygon = false
        polygons[i].options.editable = !this.disabled
        this.polygon = new this.google.maps.Polygon({
          paths: polygons[i].paths,
          options: polygons[i].options
        })
      }
    },
    onPlaceChanged(place) {
      let address = place.route
      if (place.street_number) {
        address += ` ${place.street_number}`
      }
      this.formData.street_address = address
      this.formData.postal_code = place.postal_code
      this.formData.state = place.country
      this.center = { lat: place.latitude, lng: place.longitude }
      this.preselectCityFromPlace(place)

      if (this.zoneId === -1) {
        this.resetCurrentData()
      }
    },
    /**
     * Match what is typed in the city field against the city names, ignoring
     * case and accents.
     * @param {Object} city - city item
     * @param {string} queryText - what the user typed
     */
    filterCity(city, queryText) {
      return this.normalizeCityName(city.name).includes(
        this.normalizeCityName(queryText)
      )
    },
    /**
     * Preselect the city from the address picked in the autocomplete: Google
     * returns it as locality (or one of the administrative area levels), which
     * is matched by name against the cities from the backend. Left empty when
     * nothing matches - the city field is required, so it has to be picked.
     * @param {Object} place - vue-google-autocomplete place
     */
    preselectCityFromPlace(place) {
      const candidates = [
        place.locality,
        place.administrative_area_level_2,
        place.administrative_area_level_1
      ].filter((name) => !!name)

      for (let i = 0; i < candidates.length; i++) {
        const candidate = this.normalizeCityName(candidates[i])
        const match = this.cityOptions.find(
          (city) => this.normalizeCityName(city.name) === candidate
        )
        if (match) {
          this.formData.city_id = match.id
          return
        }
      }
    },
    /**
     * Fold case and accents so the name Google returns in the browser locale
     * still matches the backend one (e.g. "Vaesteraas" / "Västerås").
     * @param {string} name - city name
     */
    normalizeCityName(name) {
      return name
        .toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // combining accents
        .replace(/ø/g, 'o')
        .replace(/æ/g, 'ae')
        .replace(/ł/g, 'l')
        // Google labels the administrative area, the cities are plain names
        .replace(/\s+(municipality|kommune|kommun|kaupunki|comune)$/, '')
        .trim()
    },
    addMarker(event) {
      this.marker.position = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      }
    },
    /**
     * get icon object for google map marker
     * @param {number} rotation - rotation for the marker
     */
    getEntryPointIcon(rotation) {
      if (this.google === null) { return }
      return {
        path: this.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 3,
        strokeColor: '#00BE14',
        fillColor: '#00BE14',
        fillOpacity: 1,
        strokeWeight: 2,
        rotation
      }
    },
    getMapObject() {
      return this.$refs.zonesMap?.$refs?.map?.$mapObject
    },
    onMapCreated() {
      const map = this.getMapObject()
      this.geocoder = new this.google.maps.Geocoder()

      // google.maps.drawing.DrawingManager was removed from the Maps JS API in
      // v3.65, so area drawing is implemented manually:
      //  - each left click drops a corner (shown as a dot)
      //  - a dashed "rubber band" segment follows the cursor to preview the
      //    next edge
      //  - clicking the first corner, or double clicking, closes the area
      //  - the finished polygon is editable, so corners and edge midpoints can
      //    be dragged (the midpoint "ghost" handles are rendered by Google)
      // Double click zoom is disabled so finishing the area does not also zoom.
      map.setOptions({ disableDoubleClickZoom: true })
      this.mapClickListener = this.google.maps.event.addListener(
        map,
        'click',
        (event) => this.onMapClickForDrawing(event)
      )
      this.mapDblClickListener = this.google.maps.event.addListener(
        map,
        'dblclick',
        () => this.finishDrawingPolygon(true)
      )
      this.mapMouseMoveListener = this.google.maps.event.addListener(
        map,
        'mousemove',
        (event) => this.onMapMouseMoveForDrawing(event)
      )

      if (this.zoneId === -1) {
        this.enableDrawingPolygon = true
      }

      if (this.zones) {
        this.updateFormDataFromLocation(this.zones[0])
        this.status_name = this.zones[0].status_name
        this.calculateEntriesFromLocation(this.zones[0])
        this.enableDrawingPolygon = false
      }
    },
    /**
     * Drop a new corner where the user clicked while in drawing mode.
     * @param {Object} event - google map click event
     */
    onMapClickForDrawing(event) {
      if (!this.enableDrawingPolygon) {
        return
      }
      this.addDrawingVertex(event.latLng)
    },
    /**
     * Move the dashed rubber-band segment so it follows the cursor from the last
     * placed corner, previewing the next edge.
     * @param {Object} event - google map mousemove event
     */
    onMapMouseMoveForDrawing(event) {
      if (
        !this.enableDrawingPolygon ||
        !this.rubberLine ||
        !this.drawingVertices.length
      ) {
        return
      }
      const lastVertex = this.drawingVertices[this.drawingVertices.length - 1]
      this.rubberLine.setPath([lastVertex, event.latLng])
    },
    /**
     * Append a corner to the in-progress area and update the preview lines/dots.
     * @param {Object} latLng - google maps LatLng
     */
    addDrawingVertex(latLng) {
      this.ensureDrawingLines()
      this.drawingVertices.push(latLng)
      this.committedLine.setPath(this.drawingVertices)
      this.rubberLine.setPath([latLng, latLng])
      this.addVertexMarker(latLng, this.drawingVertices.length === 1)
    },
    /**
     * Lazily create the solid (committed edges) and dashed (rubber band) lines.
     */
    ensureDrawingLines() {
      const map = this.getMapObject()
      if (!this.committedLine) {
        this.committedLine = new this.google.maps.Polyline({
          path: [],
          clickable: false,
          strokeColor: '#009900',
          strokeWeight: 2,
          strokeOpacity: 1,
          map
        })
      }
      if (!this.rubberLine) {
        this.rubberLine = new this.google.maps.Polyline({
          path: [],
          clickable: false,
          strokeOpacity: 0, // dashes are drawn via the repeated icon below
          icons: [
            {
              icon: {
                path: 'M 0,-1 0,1',
                strokeOpacity: 1,
                strokeColor: '#009900',
                scale: 3
              },
              offset: '0',
              repeat: '12px'
            }
          ],
          map
        })
      }
    },
    /**
     * Draw a dot at a placed corner. The first corner is clickable so the user
     * can close the area by clicking it again.
     * @param {Object} latLng - google maps LatLng
     * @param {boolean} isFirst - whether this is the first corner
     */
    addVertexMarker(latLng, isFirst) {
      const marker = new this.google.maps.Marker({
        position: latLng,
        clickable: isFirst,
        map: this.getMapObject(),
        icon: {
          path: this.google.maps.SymbolPath.CIRCLE,
          scale: isFirst ? 6 : 4,
          fillColor: isFirst ? '#009900' : '#ffffff',
          fillOpacity: 1,
          strokeColor: '#009900',
          strokeWeight: 2
        }
      })
      if (isFirst) {
        this.google.maps.event.addListener(marker, 'click', () =>
          this.finishDrawingPolygon(false)
        )
      }
      this.vertexMarkers.push(marker)
    },
    /**
     * Close the area into an editable polygon and leave drawing mode.
     * @param {boolean} fromDblClick - a double click first fires a single click
     *   that drops a corner at the finish spot; drop it when closing this way
     */
    finishDrawingPolygon(fromDblClick) {
      if (!this.enableDrawingPolygon) {
        return
      }
      // collapse consecutive duplicate corners (a double click can drop the same
      // corner twice at the exact same spot)
      this.drawingVertices = this.drawingVertices.filter(
        (v, i) => i === 0 || !v.equals(this.drawingVertices[i - 1])
      )
      // a double click also drops a corner at the finish spot — remove it so it
      // does not become an unintended extra corner
      if (fromDblClick && this.drawingVertices.length > 3) {
        this.drawingVertices.pop()
      }
      if (this.drawingVertices.length < 3) {
        return
      }
      this.polygon = new this.google.maps.Polygon({
        paths: this.drawingVertices.slice(),
        clickable: true,
        editable: true,
        draggable: true,
        strokeColor: '#009900',
        strokeWeight: 2,
        strokeOpacity: 1,
        fillColor: '#00bc00',
        fillOpacity: 0.5,
        map: this.getMapObject()
      })
      this.attachPolygonPathListeners(this.polygon)
      this.clearDrawingOverlays()
      this.enableDrawingPolygon = false
      this.updatePolygonDetails()
    },
    /**
     * Remove the in-progress preview lines and corner dots.
     */
    clearDrawingOverlays() {
      if (this.committedLine) {
        this.committedLine.setMap(null)
        this.committedLine = null
      }
      if (this.rubberLine) {
        this.rubberLine.setMap(null)
        this.rubberLine = null
      }
      this.vertexMarkers.forEach((marker) => marker.setMap(null))
      this.vertexMarkers = []
      this.drawingVertices = []
    },
    /**
     * Keep the form in sync whenever the polygon corners change (drag/add/remove).
     * @param {Object} polygon - google map polygon
     */
    attachPolygonPathListeners(polygon) {
      const path = polygon.getPath()
      ;['set_at', 'insert_at', 'remove_at'].forEach((evt) => {
        this.google.maps.event.addListener(path, evt, () => {
          this.updatePolygonDetails()
        })
      })
    },
    /**
     * Sync the drawn polygon into form data (path string + centre point).
     */
    updatePolygonDetails() {
      if (!this.polygon) {
        return
      }
      this.polygonPath = helpers.getPathsFromMvcArray(this.polygon.getPath())
      const center = this.getPolygonCenter(this.polygon)
      this.formData.centre_point = `${center.lat()} ${center.lng()}`
      this.updatePolygonStringInForm()
    },
    /**
     * Add entry point on mouse right click
     * @param {Event} $event - google map event
     */
    onMapRightClicked($event) {
      const positionAndRotation = this.getPositionAndRotation($event)
      this.entryPoints.push({
        position: positionAndRotation.position,
        icon: this.getEntryPointIcon(positionAndRotation.icon.rotation),
        delete: false
      })
    },
    onClickEntryPoint(point) {
      if (!this.disabled) {
        point.delete = true
      }
    },
    /**
     * Move the entry point to the new position when mouse drag ended
     * @param {Event} $event - google map event
     * @param {Object} point - a marker for the entry point
     */
    onDragendEntryPoint($event, point) {
      // set the dragged and rotation angle
      const positionAndRotation = this.getPositionAndRotation($event)
      point.position = positionAndRotation.position
      point.icon = {
        ...point.icon,
        rotation: positionAndRotation.icon.rotation
      }
    },
    onDragendCentrePoint($event, point) {
      // set the dragged and rotation angle
      const positionAndRotation = this.getPositionAndRotation($event)
      point.position = positionAndRotation.position
      point.icon = {
        ...point.icon
      }
    },
    /**
     * Method for finding selected polygon map object
     */
    selectedPolygonObject() {
      try {
        const zonesMap = this.$refs.zonesMap
        const polygonsRef = zonesMap.$refs.polygons
        return polygonsRef.find((p) =>
          helpers.areSameZone(p.options.zone_id, this.zone.zone_id)
        )
      } catch (e) {
        return null
      }
    },

    /**
     * When the polygon's path changed, check if there any path left, othewise enable drawing new
     */
    onPolygonPathChanged(polygon) {
      const oldOptions = polygon.options
      oldOptions.fillOpacity = 0.5
      const newPolygon = new this.google.maps.Polygon({
        paths: polygon.paths,
        options: oldOptions
      })

      this.polygonPath = helpers.getPathsFromMvcArray(newPolygon.getPath())
      this.polygon = newPolygon

      this.updatePolygonStringInForm()
      if (!this.polygonPath) {
        this.enableDrawingPolygon = true
      }
    },
    /**
     * Get the center point of polygon. Use when zone boundaries is drawn, get the boundary center to set as map center
     * @param {polygon} polygon
     */
    getPolygonCenter(polygon) {
      const bounds = new this.google.maps.LatLngBounds()
      polygon.getPath().forEach((edge) => {
        bounds.extend(edge)
      })
      return bounds.getCenter()
    },

    /**
     * Get calculated position and entry point rotation from $event
     * @param {Event} $event - google map event
     */
    getPositionAndRotation($event) {
      const closestPoint = math.getClosestPointOnPolygon(
        $event.latLng,
        this.polygon
      )
      const rotation = math.getRotation(closestPoint, this.polygon)
      return {
        position: {
          lat: closestPoint.y,
          lng: closestPoint.x
        },
        icon: {
          rotation
        }
      }
    },
    /**
     * Before loading new data, clear current data
     */
    resetCurrentData() {
      this.entryPoints = []
      if (this.polygon) {
        this.polygon.setMap(null)
        this.polygon = null
      }
      this.clearDrawingOverlays()
      this.enableDrawingPolygon = true
    },
    calculateEntriesFromLocation(location) {
      this.entryPoints = []
      const points = location.entry_points
      for (var i = 0; i < points.length; i++) {
        const [theLat, theLng] = points[i].entry_point.split(' ').map(Number)
        this.entryPoints.push({
          position: {
            lat: theLat,
            lng: theLng
          },
          icon: this.getEntryPointIcon(points[i].entry_point_angle),
          delete: false
        })
      }
    },
    updateFormDataFromLocation(location) {
      Object.assign(this.formData, {
        street_address: location.street_address,
        postal_code: location.postal_code,
        state: location.state,
        entry_points: location.entry_points,
        polygon: location.polygon,
        centre_point: location.centre_point,
        city_id: location.city_id,
        country_id: location.country_id,
        show_polygon: location.show_polygon
      })
      this.formDataCopy = JSON.stringify(this.formData)
      const path = this.getZonePaths(location)
      this.polygonPath = path
    },
    resetForm(location) {
      if (location.street_address) {
        this.$refs.autocomplete.$el.value = location.street_address
      } else {
        this.$refs.autocomplete.$el.value = ''
      }
      this.updateFormDataFromLocation(location)
      this.calculateEntriesFromLocation(location)
      this.$refs.zonesMap.setMapCenter()
    },
    updatePolygonStringInForm() {
      const polygon = []

      for (let i = 0; i < this.polygonPath.length; i++) {
        polygon.push(`${this.polygonPath[i].lat} ${this.polygonPath[i].lng}`)
      }
      this.formData.polygon = polygon.join(',')
    }
  },
  watch: {
    formData: {
      async handler(newValue) {
        const validated = newValue.polygon !== null && !!newValue.city_id
        if (this.zoneId !== -1) {
          const changed = JSON.stringify(newValue) !== this.formDataCopy
          await this.$emit('changed', changed)
        }
        this.$emit('validated', validated)
        if (validated) {
          this.$emit('formData', newValue)
        }
      },
      deep: true
    },
    enableDrawingPolygon(newVal) {
      // Drawing mode is driven by the map click listener (onMapClickForDrawing);
      // here we reflect the mode in the cursor (crosshair = "click to draw")
      // and the polygon's appearance.
      const map = this.getMapObject()
      if (map) {
        map.setOptions({ draggableCursor: newVal ? 'crosshair' : null })
      }
      if (this.polygon) {
        this.polygon.setOptions({
          fillOpacity: newVal ? 0.5 : 0.3 // 0.3 when not in drawing/edit mode, for example
        })
      }
    },
    currentLocation(newValue) {
      if (newValue) {
        this.updateFormDataFromLocation(newValue)
        this.calculateEntriesFromLocation(newValue)
        this.status_name = newValue.status_name
        this.enableDrawingPolygon = false
      }
    },
    filteredEntryPoints(newValue) {
      this.formData.entry_points = []
      if (newValue) {
        for (let i = 0; i < newValue.length; i++) {
          if (!newValue[i].delete) {
            this.formData.entry_points.push({
              entry_point: `${newValue[i].position.lat} ${newValue[i].position.lng}`,
              entry_point_angle: parseInt(
                math.getAngleFromRotation(newValue[i].icon.rotation)
              )
            })
          }
        }
      }
      this.formData.entry_points = this.formData.entry_points.filter(
        (point) =>
          helpers.isNumber(point.entry_point_angle) &&
          helpers.isString(point.entry_point)
      )
    }
  },
  computed: {
    google: gmapApi,
    cities() {
      return this.$store.getters.cities || []
    },
    merchantCountryCode() {
      const merchant = this.$store.getters.currentMerchant
      if (merchant?.country_code) {
        return merchant.country_code
      }
      // older payloads only carry the id
      const country = this.$store.getters.countries.find(
        (item) => item.id === merchant?.country_id
      )
      return country?.code
    },
    merchantTimezone() {
      return COUNTRY_TIMEZONES[this.merchantCountryCode]
    },
    cityHint() {
      return this.merchantTimezone
        ? `Parking times are recorded in the city's timezone (${this.merchantTimezone})`
        : ''
    },
    /**
     * Cities in the merchant country's timezone, plus whichever city the
     * location already has, so an existing (possibly wrong) one stays visible
     * and changeable. Filtering on the timezone rather than the country is
     * deliberate: the cities endpoint returns no country, and several of its
     * rows are tagged with a timezone that does not match their name - hiding
     * those is exactly what keeps parking times right. Falls back to every city
     * when the merchant's country or timezone matches none, so the picker is
     * never empty.
     */
    cityOptions() {
      const named = this.cities.filter((city) => !!city.name)
      const inTimezone = this.merchantTimezone
        ? named.filter(
            (city) =>
              city.timezone === this.merchantTimezone ||
              city.id === this.formData.city_id
          )
        : []
      const cities = inTimezone.length ? inTimezone : named

      return [...cities].sort((a, b) => a.name.localeCompare(b.name))
    },
    filteredEntryPoints() {
      return this.entryPoints.filter((m) => !m.delete)
    },
    locationId() {
      return this.$route.params.locationId
    },
    currentLocation() {
      return this.$store.getters.currentLocation
    },
    isReviewing() {
      if (this.zone) {
        return this.status_name === 'pending'
      } else {
        return false
      }
    },
    isWaitingForDeletion() {
      if (this.zone) {
        return this.status_name === 'waiting_for_deletion'
      } else {
        return false
      }
    }
  },
  beforeDestroy() {
    if (this.mapClickListener) {
      this.google.maps.event.removeListener(this.mapClickListener)
    }
    if (this.mapDblClickListener) {
      this.google.maps.event.removeListener(this.mapDblClickListener)
    }
    if (this.mapMouseMoveListener) {
      this.google.maps.event.removeListener(this.mapMouseMoveListener)
    }
    this.clearDrawingOverlays()
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/common';
.d-flex {
  display: flex;
}
.flex-grow-1 {
  flex-grow: 1;
}
.form-label {
  padding-top: 18px;
  font-style: normal;
  font-weight: 500;
  font-size: 10px;
  line-height: 120%;
  color: rgba(31, 31, 31, 0.5);
}
.map-label {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  color: #333333;
}
.fill-space {
  height: 100%;
}
.map-form {
  display: flex;
  flex-direction: column;
  height: 100% !important;
}

.status-bar {
  padding-bottom: 32px;
}
.form-wrapper {
  width: 339px !important;
  height: 100% !important;
  padding-top: 32px;
}

.map-container {
  padding-left: 56px;
  box-sizing: border-box;
}

.submit-button {
  position: absolute;
  bottom: 24px;
  width: 100%;
  max-width: 339px;
}

.city-select {
  width: 339px;
}

.custom-suggestion {
  width: 339px;
  height: 32px;
  padding: 6px 8px;
  background: #f6f6f6;
  border-radius: 2px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  .suggestion-content {
    flex: 1;
    color: #333333;
    font-size: 14;
    font-family: Roboto;
    font-weight: 500;
    line-height: 1.4;
    word-wrap: break-word;
  }
  &.has-error {
    border: 1px solid #eb5757 !important;
  }
}
</style>
<style lang="scss">
.map-form {
  .hidden-form {
    padding-top: 0;
    .v-input__slot {
      display: none;
    }
  }
}

.toggle-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toggle-label {
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  color: #333333;
}
</style>
